import { useEffect } from 'react';
import { useParams, useLocation, Navigate, Outlet } from 'react-router-dom';
import { useCurrentProjectWriter } from '../../utils/redux/currentProjectService';
import { INVITATION_EXPIRY_MS } from '../../utils/postAuthRoute';

/**
 * Layout route element for the project-scoped route subtree
 * (/:projectId/grant-access, /:projectId/analyze/*,
 * /:projectId/settings) -- see
 * docs/product/bugs/connect-cta-new-tab-project-leak/trd.md Section 3,
 * R2.
 *
 * The URL's :projectId path segment is the source of truth for
 * `currentProject` (TRD Section 2.3): this is the only place that writes
 * it for the general case (the Shopify OAuth round trip is the sole
 * exception -- see utils/shopifyGrantFlowProject.js). Runs on mount and
 * on every :projectId param change. A brand-new browser tab gets its own
 * URL carrying its own project ID, independent of whatever any
 * local/session storage in that fresh browsing context happens to hold --
 * this is what closes the new-tab-project-leak bug this TRD fixes.
 *
 * Props:
 *   projects        - user.projects (array)
 *   pendingInvitations  - user.pending_invitations (array)
 */
const ProjectRouteGuard = ({ projects, pendingInvitations }) => {
  const { projectId } = useParams();
  const location = useLocation();
  const { setCurrentProject } = useCurrentProjectWriter();

  const list = projects || [];
  const matched = list.find((sf) => sf.id === projectId);

  // Dispatching in an effect (not during render) keeps this a pure render
  // function -- the actual Redux write happens as a side effect, keyed on
  // the matched project so it re-fires whenever :projectId changes to
  // a different valid entry (or the fresh entry's own fields change on a
  // profile refetch).
  useEffect(() => {
    if (matched) {
      setCurrentProject(matched);
    }
  }, [matched, setCurrentProject]);

  if (matched) {
    return <Outlet />;
  }

  // No match: foreign, stale, or malformed project ID (covers a
  // post-signup redirect_url whose embedded project ID doesn't match the
  // freshly-authenticated user's projects, and any tampered/copy-pasted
  // URL). Same precedence getPostAuthRoute already encodes, but preserving
  // the rest of the URL rather than bouncing to a fixed landing page (TRD
  // AC-3, AC-4, AC-5, AC-6).
  if (list.length === 0) {
    const now = Date.now();
    const hasNonExpiredInvitation = (pendingInvitations || []).some(
      (inv) => now - Number(inv.created_at) < INVITATION_EXPIRY_MS
    );
    return <Navigate to={hasNonExpiredInvitation ? '/accept-invite' : '/create-project'} replace />;
  }

  const target = location.pathname.replace(/^\/[^/]+/, `/${list[0].id}`) + location.search;
  return <Navigate to={target} replace />;
};

export default ProjectRouteGuard;
